'use client';

import { ACCOUNT_COPY } from '@eliadmin/core/src/components/dlz/account/account-copy';
import { AccountHeading } from '@eliadmin/core/src/components/dlz/account/account-heading';
import { FAVORITES_COPY } from '@eliadmin/core/src/components/dlz/account/favorites/favorites-copy';
import {
  FavoritesStateCard,
  STATE_CARD_BUTTON,
} from '@eliadmin/core/src/components/dlz/account/favorites/favorites-state-card';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/**
 * Figma „Benefits“ (60325:22027, mobile 60459:98865): one row on desktop, a list below – full width
 * on phones, centred as a block on tablets.
 */
function Benefits() {
  return (
    <ul className="flex w-full flex-col gap-2 md:w-auto lg:flex-row lg:gap-8">
      {FAVORITES_COPY.guest.benefits.map(benefit => (
        <li key={benefit.text} className="flex items-center gap-2 text-left">
          {/* The Figma instances are scaled to 24 px with a 1.8 px stroke. */}
          <Icon
            name={benefit.icon}
            size={24}
            className="text-[#D7191F] [&_[stroke]]:[stroke-width:1.8]"
          />
          <span className="dlz-body-s text-[#0F1012]">{benefit.text}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * „Obľúbené produkty“ of a guest (Figma desktop 60325:21369, mobile 60459:98469). A guest keeps no
 * favourites (the store saves them for signed-in customers only), so the page invites to sign in.
 * It renders outside the account shell with the same frame: breadcrumb „Domov › Obľúbené produkty“
 * 40 px under the header and the card 32 px below it; under `lg` the „‹ Obľúbené produkty“ link
 * (back home) and the page heading instead. The heading has no „8 produktov · …“ subtitle here – a
 * guest has no list to count – and stays the page's h1 for screen readers on desktop.
 */
export function FavoritesGuestView() {
  const { t } = useTranslations();
  const title = ACCOUNT_COPY.pages.favorites.label;
  const [titleStart, titleEnd] = FAVORITES_COPY.guest.title;

  return (
    <div className="mx-auto flex w-full max-w-[1392px] flex-col gap-6 px-4 pb-6 pt-12 md:px-6 lg:gap-8 lg:px-8 lg:pb-10 lg:pt-10">
      <Breadcrumbs
        breadcrumb={{ name: title, slug: null }}
        className={{ wrapper: 'hidden px-0 md:px-0 lg:block lg:px-0' }}
      />
      <Link
        href={ROUTES.HOME()}
        prefetch={false}
        className="flex items-center gap-2.5 self-start rounded-sm text-[#D7191F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] lg:hidden"
      >
        <Icon name="caret-left" size={20} />
        <span className="dlz-h5">
          <span className="sr-only">{FAVORITES_COPY.guest.back}: </span>
          {title}
        </span>
      </Link>
      <AccountHeading title={title} className="lg:sr-only" />
      <FavoritesStateCard
        icon="heart"
        title={
          <>
            {titleStart} <br className="max-md:hidden" />
            {titleEnd}
          </>
        }
        text={FAVORITES_COPY.guest.text}
        actions={
          <>
            <Button asChild className={STATE_CARD_BUTTON}>
              <Link href={ROUTES.SIGN_IN(ROUTES.FAVORITES())} prefetch={false}>
                {t('text189')}
              </Link>
            </Button>
            <Button asChild variant="secondary" className={STATE_CARD_BUTTON}>
              <Link href={ROUTES.REGISTER()} prefetch={false}>
                {t('text758')}
              </Link>
            </Button>
          </>
        }
      >
        <Benefits />
      </FavoritesStateCard>
    </div>
  );
}
